/**
 * Automatic Error Logger
 * Captures JavaScript errors, unhandled promise rejections, and API failures
 * for inclusion in bug reports
 */

class ErrorLogger {
  constructor(maxErrors = 20) {
    this.errors = [];
    this.maxErrors = maxErrors;
    this.initialized = false;
  }

  initialize() {
    if (this.initialized) return;

    // Capture JavaScript errors
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'JavaScript Error',
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack,
        timestamp: new Date().toISOString()
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'Unhandled Promise Rejection',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        timestamp: new Date().toISOString()
      });
    });

    // Capture console errors (optional - intercept console.error)
    const originalConsoleError = console.error;
    console.error = (...args) => {
      this.logError({
        type: 'Console Error',
        message: args.map(arg =>
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' '),
        timestamp: new Date().toISOString()
      });
      originalConsoleError.apply(console, args);
    };

    this.initialized = true;
    console.log('[ErrorLogger] Initialized - capturing errors automatically');
  }

  logError(error) {
    // Add to circular buffer
    this.errors.push(error);

    // Keep only last N errors
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }
  }

  getRecentErrors(count = 10) {
    return this.errors.slice(-count);
  }

  getAllErrors() {
    return [...this.errors];
  }

  clearErrors() {
    this.errors = [];
  }

  formatErrorsForReport() {
    if (this.errors.length === 0) {
      return 'No errors captured';
    }

    return this.errors.map((error, index) => {
      const parts = [
        `### Error ${index + 1}: ${error.type}`,
        `**Time**: ${new Date(error.timestamp).toLocaleString()}`,
        `**Message**: ${error.message}`
      ];

      if (error.source) {
        parts.push(`**Source**: ${error.source}:${error.line}:${error.column}`);
      }

      if (error.stack) {
        parts.push(`**Stack Trace**:\n\`\`\`\n${error.stack}\n\`\`\``);
      }

      return parts.join('\n');
    }).join('\n\n---\n\n');
  }

  hasErrors() {
    return this.errors.length > 0;
  }
}

// Singleton instance
const errorLogger = new ErrorLogger();

export default errorLogger;
